import { useMemo, useState } from 'react';
import { BookOpen, Check, Eye, Landmark, Pencil, Sparkles, Trash2 } from 'lucide-react';
import { getQuestionTypeLabel } from '../../../utils/questionUtils';
import { toPayloadUnifie } from '../../../utils/evalNatAdapter';
import { supportDeQuestion } from '../../../generators/evalnat/supports';
import ExercicePreview from '../../VueQuestion/ExercicePreview';
import ZoomableMedia from '../../VueQuestion/ZoomableMedia';
import FenetreTexteSupport from './FenetreTexteSupport';

/**
 * Carte d'une question EvalNat — gabarit "exerciseur" (2.4c).
 *
 * L'exercice s'affiche EN ENTIER dans la carte (énoncé + réponses, bonnes
 * réponses visibles), comme les cartes de la Banque TanoEd : plus de texte
 * tronqué ni de clic obligatoire sur l'œil pour comprendre la question.
 * Pas de bouton "Autre variante" : les questions EvalNat sont FIGÉES (on
 * rejoue toujours la même) — la régénération n'existe pas ici.
 *
 * Le rendu passe par le payload unifié (toPayloadUnifie → ExercicePreview,
 * le même moteur d'aperçu que l'exerciseur) — dérivation pure, mémoïsée.
 */
const QuestionCardEvalNat = ({ question, selectable, selected, onToggle, onPreview, onEdit, onDelete, provenance = null, avecTexte = true }) => {
  const { question_num, est_nationale } = question;
  // Nationale, ou commune à tous les collèges (forme parallèle, établissement vide) : ni modifiée ni supprimée
  // depuis un collège (11/10/2026)
  const modifiable = !est_nationale && question.etablissement !== null;

  // Question EvalNat → format unifié (énoncé/réponses rendus par ExercicePreview)
  const payloadUnifie = useMemo(() => toPayloadUnifie(question), [question]);
  // Une variante de compréhension porte son propre texte (utils/supportLecture.js) : le prof le lit ici,
  // sauf quand la page le propose déjà au-dessus (forme parallèle : avecTexte = false)
  const support = useMemo(() => (avecTexte ? supportDeQuestion(question) : null), [question, avecTexte]);
  const [texteOuvert, setTexteOuvert] = useState(false);

  const handleCardClick = () => {
    if (selectable) onToggle(question.id);
  };

  return (
    <div
      onClick={handleCardClick}
      className={`relative group rounded-xl border transition-all duration-200 overflow-hidden ${
        selectable ? 'cursor-pointer' : ''
      } ${
        selected
          ? 'border-indigo-400 bg-indigo-50/30 shadow-sm ring-1 ring-indigo-200'
          : 'border-gray-200 bg-white hover:shadow-md hover:border-gray-300'
      }`}
    >
      {/* Checkbox sélection */}
      {selectable && (
        <div className="absolute top-2.5 left-2.5 z-10">
          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
            selected
              ? 'bg-indigo-500 border-indigo-500'
              : 'border-gray-300 bg-white group-hover:border-indigo-300'
          }`}>
            {selected && <Check size={12} strokeWidth={3} className="text-white" />}
          </div>
        </div>
      )}

      {/* Actions overlay */}
      <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        {onPreview && (
          <button
            onClick={(e) => { e.stopPropagation(); onPreview(question); }}
            aria-label="Aperçu plein écran"
            title="Aperçu plein écran"
            className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-500 hover:bg-indigo-100 flex items-center justify-center transition"
          >
            <Eye size={13} />
          </button>
        )}
        {modifiable && onEdit && (
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(question); }}
            aria-label="Modifier"
            className="w-7 h-7 rounded-lg bg-amber-50 text-amber-500 hover:bg-amber-100 flex items-center justify-center transition"
          >
            <Pencil size={13} />
          </button>
        )}
        {modifiable && onDelete && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(question); }}
            aria-label="Supprimer"
            className="w-7 h-7 rounded-lg bg-orange-50 text-orange-400 hover:bg-orange-100 hover:text-orange-500 flex items-center justify-center transition"
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>

      {/* En-tête badges : numéro + provenance + type */}
      <div className={`px-4 pt-3 flex items-center gap-1.5 flex-wrap pr-16 ${selectable ? 'pl-9' : ''}`}>
        {question_num && (
          <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-blue-100 text-blue-600 uppercase">
            {question_num}
          </span>
        )}
        {provenance ? (
          // Une question d'une forme parallèle (11/10/2026) : sa provenance, ni nationale ni variante
          <span className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded bg-sky-100 text-sky-700">
            <BookOpen size={9} />
            {provenance}
          </span>
        ) : est_nationale ? (
          <span className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded bg-emerald-100 text-emerald-600">
            <Landmark size={9} />
            Nationale
          </span>
        ) : question.generator_key ? (
          // Variante fabriquée par l'exerciseur de l'item (src/generators/evalnat)
          <span className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded bg-indigo-100 text-indigo-600">
            <Sparkles size={9} />
            Variante générée
          </span>
        ) : (
          <span className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-100 text-amber-600">
            <Pencil size={9} />
            Variante
          </span>
        )}
        <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-indigo-100 text-indigo-600 uppercase">
          {getQuestionTypeLabel(question)}
        </span>
      </div>

      {/* Corps : l'exercice complet, rendu comme dans l'exerciseur.
          TEMPORAIRE (12/06/2026) : les questions nationales importées portent
          souvent l'ÉNONCÉ COMPLET dans `image` (capture — choix inclus dedans) ;
          texte/reponses y sont vides ou redondants. Dans ce cas on n'affiche
          QUE l'image (sélection, badges et actions conservés). À retirer quand
          les questions EvalNat seront ressaisies en format structuré. */}
      <div className={`px-4 py-3 ${selectable ? 'pl-9' : ''}`}>
        {question.image ? (
          /* Dimensionnement "contenu" : le CONTENEUR impose sa loi —
             - object-contain : image entière, ratio préservé (jamais de crop
               sur un énoncé) ;
             - max-h-80 : clamp de hauteur, une capture A4 ne déforme pas la
               grille ;
             - max-w-full + w-auto + mx-auto : pas d'upscale flou des petites
               images, centrées à taille naturelle ;
             - ZoomableMedia : clic → plein écran pour la lisibilité fine. */
          <div onClick={(e) => e.stopPropagation()} className="cursor-zoom-in">
            <ZoomableMedia
              type="image"
              src={question.image}
              alt={`Question ${question_num || ''}`}
              className="w-full rounded-lg border border-gray-100 bg-white overflow-hidden"
              imgClassName="max-w-full w-auto h-auto max-h-80 object-contain mx-auto"
            />
          </div>
        ) : payloadUnifie ? (
          <ExercicePreview question={payloadUnifie} />
        ) : (
          <p className="text-sm text-gray-400 italic">Question illisible</p>
        )}
      </div>

      {/* Le texte d'une variante de compréhension, dans une fenêtre (portail : hors du clic de la carte) */}
      {support ? (
        <div className={`px-4 pb-3 ${selectable ? 'pl-9' : ''}`}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setTexteOuvert(true); }}
            className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition"
          >
            <BookOpen size={13} />
            Lire le texte de la variante : {support.titre}
          </button>
        </div>
      ) : null}
      {support && texteOuvert ? <FenetreTexteSupport support={support} onClose={() => setTexteOuvert(false)} /> : null}
    </div>
  );
};

export default QuestionCardEvalNat;
